import type { BmiLimits } from './calculations';
export type GainBand = { minBmi: number; maxBmi: number | null; lowLb: number; highLb: number };
export interface WeightGainTrajectory {
  fetusCount: number;
  minBmi: number;
  maxBmi: number | null;
  citation: string;
  points: { gaDays: number; lowKg: number; highKg: number }[];
}
/** Linear interpolation only within explicitly reviewed points; never extrapolates. */
export function trajectoryRange(
  trajectories: WeightGainTrajectory[],
  bmi: number,
  fetusCount: number,
  days: number,
): [number, number] | null {
  const band = trajectories.find(
    (b) => b.fetusCount === fetusCount && bmi >= b.minBmi && (b.maxBmi === null || bmi < b.maxBmi),
  );
  if (!band || band.points.length < 2 || days < band.points[0].gaDays || days > band.points.at(-1)!.gaDays) return null;
  const upper = band.points.findIndex((point) => point.gaDays >= days);
  if (upper === 0) return [band.points[0].lowKg, band.points[0].highKg];
  const a = band.points[upper - 1],
    b = band.points[upper],
    fraction = (days - a.gaDays) / (b.gaDays - a.gaDays);
  return [a.lowKg + (b.lowKg - a.lowKg) * fraction, a.highKg + (b.highKg - a.highKg) * fraction];
}
export interface ClinicalReferences {
  weight_gain_trajectories?: WeightGainTrajectory[];
  enabled: boolean;
  citation: string;
  bmi_limits: BmiLimits;
  singleton_gain_bands: GainBand[];
  twin_gain_bands: GainBand[];
  redating_bands: { maxDays: number; differenceDays: number }[];
  fh_window: { minDays: number; maxDays: number; toleranceCm: number };
  bp_reference: { warnSystolic: number; warnDiastolic: number; criticalSystolic: number; criticalDiastolic: number };
  fetal_heart_range: { minBpm: number; maxBpm: number };
  postdates: { warnDays: number; criticalDays: number };
}
export type PrenatalAlertId =
  | 'R-PN-01'
  | 'R-PN-02'
  | 'R-PN-03'
  | 'R-PN-04'
  | 'R-PN-05'
  | 'R-PN-06'
  | 'R-PN-07'
  | 'R-PN-08'
  | 'R-PN-09'
  | 'R-PN-10';
